import React from 'react';
import { Box, Typography } from '@mui/material';
import { calculateForecastAndVariance } from './forecastMath';

const formatHours = (value, maximumFractionDigits = 1) =>
  (value || 0).toLocaleString(undefined, { maximumFractionDigits });

const formatNullableHours = (value, maximumFractionDigits = 1) =>
  value == null || !Number.isFinite(value)
    ? '—'
    : value.toLocaleString(undefined, { maximumFractionDigits });

const cardSx = {
  p: 1.25,
  border: '0.5px solid',
  borderColor: 'divider',
  borderRadius: 1,
  bgcolor: 'background.paper',
  // Equal-height blocks so the numbers are evenly spaced down the card.
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
};

/**
 * Summary column: total budget, actual, earned hours and budget variance
 * (nested stat blocks). Budget variance uses an effective total % complete of
 * earned / budget, so it won't equal Shop + Field variance when both exist.
 * Outer surface is provided by the parent Card in WorkPackageProgressModal.
 */
const TotalColumn = ({ budgetHrs, actualHrs, earnedHrs }) => {
  const totalPct = budgetHrs > 0 ? (earnedHrs / budgetHrs) * 100 : 0;
  const { budgetVarianceHrs } = calculateForecastAndVariance(
    budgetHrs,
    actualHrs,
    totalPct
  );
  const hasVariance =
    budgetVarianceHrs != null && Number.isFinite(budgetVarianceHrs);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        height: '100%',
      }}
    >
      <Box sx={cardSx}>
        <Typography sx={{ fontSize: '10px', color: 'text.secondary' }}>
          Budget
        </Typography>
        <Typography
          sx={{ fontSize: '17px', fontWeight: 700, lineHeight: 1.25, mt: 0.25 }}
        >
          {formatHours(budgetHrs, 0)}
          <Box
            component="span"
            sx={{
              fontSize: '0.62em',
              color: 'text.secondary',
              fontWeight: 500,
              ml: 0.5,
            }}
          >
            hrs
          </Box>
        </Typography>
      </Box>
      <Box sx={cardSx}>
        <Typography sx={{ fontSize: '10px', color: 'text.secondary' }}>
          Actual
        </Typography>
        <Typography
          sx={{ fontSize: '17px', fontWeight: 700, lineHeight: 1.25, mt: 0.25 }}
        >
          {formatHours(actualHrs, 1)}
          <Box
            component="span"
            sx={{
              fontSize: '0.62em',
              color: 'text.secondary',
              fontWeight: 500,
              ml: 0.5,
            }}
          >
            hrs
          </Box>
        </Typography>
      </Box>
      <Box sx={cardSx}>
        <Typography sx={{ fontSize: '10px', color: 'text.secondary' }}>
          Earned
        </Typography>
        <Typography
          sx={{ fontSize: '17px', fontWeight: 700, lineHeight: 1.25, mt: 0.25 }}
        >
          {formatHours(earnedHrs, 1)}
          <Box
            component="span"
            sx={{
              fontSize: '0.62em',
              color: 'text.secondary',
              fontWeight: 500,
              ml: 0.5,
            }}
          >
            hrs
          </Box>
        </Typography>
      </Box>
      <Box sx={cardSx}>
        <Typography sx={{ fontSize: '10px', color: 'text.secondary' }}>
          Budget Variance
        </Typography>
        <Typography
          sx={{
            fontSize: '17px',
            fontWeight: 700,
            lineHeight: 1.25,
            mt: 0.25,
            color:
              hasVariance && budgetVarianceHrs < 0 ? 'error.main' : 'inherit',
          }}
        >
          {formatNullableHours(budgetVarianceHrs, 1)}
          {hasVariance && (
            <Box
              component="span"
              sx={{
                fontSize: '0.62em',
                color: 'text.secondary',
                fontWeight: 500,
                ml: 0.5,
              }}
            >
              hrs
            </Box>
          )}
        </Typography>
      </Box>
    </Box>
  );
};

export default TotalColumn;
